import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { BeeChatClient } from '../helper-bee/BeeChatClient';
import { stripCitations } from '../helper-bee/BeeChatState';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { CareMapState } from '../caremap/CareMapState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { Logger } from '../support/Logger';
import { ICareMap } from '../caremap/ICareMap';
import { WithLinks } from '../caremap/WithLinks';
import { ICaremapNoteResponse } from '../notes/model/NotesClient';
import { ICaseloadClient, ICaseloadSearchRow, ICaseloadVerdict } from './ICaseloadSearch';
import { buildNameQueryTokens, matchesNameQuery } from './clientNameMatch';
import { parseVerdict } from './parseVerdict';

const CAREMAP_MEDIA_TYPE = 'application/vnd.innerhive.caremap.v3+json';
const NOTE_MEDIA_TYPE = 'application/vnd.innerhive.caremap-note+json';

// Compared loosely because the model re-punctuates file names it read; only the letters and digits
// carry the identity.
const normaliseFileName = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '');

// Each client costs one authorized chat turn, so a book of fifty cannot be asked in one go. The cap
// bounds that; the surface states the cap and which clients were covered rather than trimming quietly.
//
// Set above a typical book rather than well under one, because the clients dropped are whichever sort
// last by name, which has nothing to do with who the question is about. A caseload that fits answers
// completely; only an unusually large one is trimmed, and it says so.
export const MAX_CLIENTS_PER_SEARCH = 30;

// Enough parallelism to keep a caseload-sized question interactive, low enough not to open a burst of
// streaming requests that then all time out together.
const MAX_CONCURRENT_CLIENT_SEARCHES = 5;

// How long one client's answer may take before that client alone is given up on.
//
// Nothing is shown until every client has been read, so without this a single stream that never
// closes holds the entire answer for ever: the section sits there with a Stop button and no results,
// which is indistinguishable from the feature being broken. One client timing out costs that row and
// says so; the rest of the caseload still answers.
const CLIENT_SEARCH_TIMEOUT_MS = 60_000;

// The same question is asked once per client, with no conversation around it to steer the answer, so
// the wrapper has to carry what a chat would otherwise negotiate: stay inside this client's own
// records, stay short enough to scan in a list, and state an absence outright instead of talking
// around it. A confident-sounding non-answer is the failure mode this is guarding against.
const buildClientQuestion = (params: { question: string; clientName: string }): string => {
    const { question, clientName } = params;
    return [
        `Question about ${clientName}: ${question}`,
        '',
        `Answer only from ${clientName}'s own Innerhive records. Do not use web search.`,
        // Facts like code status, insurance and identity documents live on the care profile as
        // fields, not in the written record, so a content-only search reports them as absent when
        // they are recorded. Searching notes alone answered "no DNR on file" for clients whose
        // profile says otherwise, which is worse than no answer.
        'Check the care profile as well as notes, recordings and documents. Code status, insurance,',
        'Medicaid and identity documents are recorded as profile fields rather than written up.',
        '',
        // The caller is assembling one answer across a caseload, so each client has to reduce to
        // something sortable. The verdict comes first and alone on its line so a partial stream can
        // be classified before the sentence has finished arriving.
        'Reply in exactly this format:',
        'Line 1: one word, YES or NO or UNCLEAR. YES if their records answer the question',
        'affirmatively, NO if their records positively rule it out, UNCLEAR only if you genuinely',
        'cannot tell.',
        // A record that exists but is unverified is still a match, and the doubt belongs in the
        // reason rather than in a separate bucket. Sorting it away from the matches hides the case
        // a fiduciary most needs to see: the fact everyone relies on that nobody has confirmed.
        'If the record exists but is unconfirmed, out of date, or the supporting document cannot be',
        'found, that is still YES. Say so plainly in the reason.',
        'Line 2: one sentence, at most 25 words, giving the reason and naming the record it came from.',
        // The caller resolves this against the client's own files to build a link, so it has to be
        // the file's name as stored rather than a description of it.
        'Line 3: the exact file name of the document you relied on, or NONE if the answer did not',
        'come from an uploaded document.',
        // Every client is asked the same question, so one naming a different person has to be able
        // to rule itself out. Without this each client answers about themselves and a question about
        // one person returns an opinion about all of them.
        `If the question is about a specific person who is not ${clientName}, answer NO on line 1 and`,
        `say on line 2 only that the question is not about ${clientName}.`,
        'Do not speculate and do not add anything else.',
        // Line 2 is rendered verbatim under the client's name, so it is product copy and the house
        // rule against em dashes applies to it exactly as it does to a hand-written string. Kept
        // clear of the Line 1/2/3 block, which the reply parser reads as a contiguous format.
        'Do not use em dashes anywhere in your reply. Use a comma, colon or period instead.'
    ].join('\n');
};

interface ICaseloadSearchStateData {
    // What is typed in the caseload field right now. Distinct from `question`, which is the text the
    // rows below actually answer: the two differ from the first keystroke after a search, and the
    // roster filters on this one while the results still belong to that one. Held here rather than
    // in the field's own component because the client list narrows on it too.
    query: string;
    question: string;
    rows: ICaseloadSearchRow[];
    isRunning: boolean;
    // How many clients the member could have searched, so a capped run can say what it left out.
    caseloadSize: number;
}

export class CaseloadSearchState extends State<ICaseloadSearchStateData> {
    static inject = () => [
        BeeChatClient,
        APIClient,
        OrganizationsState,
        CareMapState,
        MixPanelEventEmitter,
        Logger
    ];

    private abortController: AbortController | null = null;

    constructor(
        private chatClient: BeeChatClient,
        private apiClient: APIClient,
        private orgState: OrganizationsState,
        private careMapState: CareMapState,
        private mixpanel: MixPanelEventEmitter,
        private log: Logger
    ) {
        super({
            query: '',
            question: '',
            rows: [],
            isRunning: false,
            caseloadSize: 0
        });
    }

    // Any divergence from the question the rows answer invalidates them, whether the text was edited,
    // deleted or cleared. Comparing against `question` rather than watching for deletions means
    // retyping the same text is not treated as a change.
    setQuery = (query: string) => {
        this.setState({ query });
        if (this.state.rows.length > 0 && query.trim() !== this.state.question) {
            this.setState({ question: '', rows: [], caseloadSize: 0 });
        }
    };

    // The clients this team member may search: the care maps the organization returned for them,
    // which is the whole book (every team member can reach every client), not a per-member subset.
    // A care map's `self` link is the server's own statement that this member may read it, so an
    // entry without one is dropped rather than asked for. That filter is a courtesy only. Every
    // search below is authorized again, per care map, by the API.
    getCaseload = (): ICaseloadClient[] => {
        const org = this.orgState.getMostRecentOrganization();
        if (!org) {
            return [];
        }
        return this.orgState.getOrganizationCareMaps(org.orgId)
            .filter(careMap => Boolean(careMap.links?.self))
            .map(careMap => ({
                careMapKey: { userId: careMap.userId, careMapId: careMap.careMapId },
                name: this.careMapState.getPatientFullName(careMap) || 'Unnamed client'
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    isCaseloadCapped = (): boolean => this.state.caseloadSize > this.state.rows.length;

    /**
     * The clients a question is actually about. A question naming someone is a question about them,
     * so it is asked of them alone.
     *
     * Asking the whole book regardless costs one authorized chat turn per client and returns a stack
     * of "this is not about that person" answers, which is slow enough to look broken and drags in
     * failures from clients the question never concerned. Falls back to the whole caseload when the
     * question names nobody, which is the case these searches exist for.
     */
    getTargetedCaseload = (question: string): ICaseloadClient[] => {
        const caseload = this.getCaseload();
        const queryTokens = buildNameQueryTokens(question);
        const named = caseload.filter(client => matchesNameQuery(client.name, queryTokens));
        return named.length > 0 ? named : caseload;
    };

    getClientsToSearch = (question: string): ICaseloadClient[] => {
        return this.getTargetedCaseload(question).slice(0, MAX_CLIENTS_PER_SEARCH);
    };

    // The caseload reduced to the shape a case manager reads: who matched, who did not settle it,
    // and who was ruled out. Ordered so the clients needing something come first.
    getByVerdict = (verdict: ICaseloadVerdict): ICaseloadSearchRow[] => {
        return this.state.rows.filter(row => row.status === 'answered' && row.verdict === verdict);
    };

    getUnavailableRows = (): ICaseloadSearchRow[] => {
        return this.state.rows.filter(row => row.status === 'unavailable');
    };

    getPendingCount = (): number => {
        return this.state.rows.filter(row => row.status === 'waiting' || row.status === 'searching').length;
    };

    /** Clients whose answer has landed, either way. What the progress line counts. */
    getReadCount = (): number => {
        return this.state.rows.filter(row => row.status === 'answered' || row.status === 'unavailable').length;
    };

    // Results only mean anything next to the question that produced them, so editing the question
    // has to take them away. Leaving them on screen under changed text presents an answer to a
    // question nobody asked.
    clear = () => {
        this.cancel();
        this.setState({ query: '', question: '', rows: [], isRunning: false, caseloadSize: 0 });
    };

    cancel = () => {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    };

    run = async (question: string): Promise<void> => {
        const trimmedQuestion = question.trim();
        if (!trimmedQuestion || this.state.isRunning) {
            return;
        }

        const caseload = this.getCaseload();
        const targeted = this.getTargetedCaseload(trimmedQuestion);
        const clientsToSearch = targeted.slice(0, MAX_CLIENTS_PER_SEARCH);
        if (clientsToSearch.length === 0) {
            return;
        }

        this.cancel();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.setState({
            query: trimmedQuestion,
            question: trimmedQuestion,
            // what this answer was meant to cover, so a capped run reports against the set it was
            // asked of rather than against the whole book it deliberately narrowed away from
            caseloadSize: targeted.length,
            isRunning: true,
            rows: clientsToSearch.map(client => ({ client, status: 'waiting', answer: '' }))
        });

        this.mixpanel.track('Caseload Search Run', () => ({
            'Clients Searched': clientsToSearch.length,
            'Caseload Size': caseload.length,
            'Question Length': trimmedQuestion.length,
            'Narrowed To Named Clients': targeted.length < caseload.length
        }));

        try {
            const entry = await this.apiClient.entry();
            const streamUrl = entry.links.chatStream.href;

            await this.searchWithConcurrencyLimit({
                clients: clientsToSearch,
                run: client => this.searchOneClient({ client, question: trimmedQuestion, streamUrl, signal }),
                signal
            });
        } finally {
            this.abortController = null;
            this.setState({ isRunning: false });
        }
    };

    // One authorized chat turn per client, scoped to that client's care map. The care map key is what
    // the API authorizes the turn against, so a client this member may not read fails as this row
    // rather than leaking into another client's answer.
    private searchOneClient = async (params: {
        client: ICaseloadClient;
        question: string;
        streamUrl: string;
        signal: AbortSignal;
    }): Promise<void> => {
        const { client, question, streamUrl, signal } = params;

        this.updateRow(client, row => ({ ...row, status: 'searching' }));

        let answer = '';

        // Chained to the run's controller rather than replacing it, so Stop still aborts every client
        // and the timeout only ever gives up on this one.
        //
        // An AbortSignal fires `abort` exactly once, at abort() time, so a listener registered on an
        // already-aborted signal never runs at all. The worker loop below already stops handing out
        // clients once Stop lands, so this keeps the method correct for any other caller rather than
        // carrying that guarantee on its own.
        const perClient = new AbortController();
        const abortThisClient = () => perClient.abort();
        if (signal.aborted) {
            perClient.abort();
        }
        signal.addEventListener('abort', abortThisClient);
        const timeout = setTimeout(abortThisClient, CLIENT_SEARCH_TIMEOUT_MS);

        try {
            for await (const event of this.chatClient.streamChat({
                streamUrl,
                message: buildClientQuestion({ question, clientName: client.name }),
                careMapKey: client.careMapKey,
                signal: perClient.signal,
                metadata: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
            })) {
                if (event.type === 'text' && event.text) {
                    // Accumulated but not written through. The row stays pending until the whole
                    // reply is parsed, because a half-arrived "NO" would render as a verdict when
                    // it is a fragment of one.
                    answer += event.text;
                } else if (event.type === 'error') {
                    throw new Error(event.message || 'The search could not be completed.');
                }
            }

            const finalAnswer = stripCitations(answer, false).trim();
            if (!finalAnswer) {
                this.markUnavailable({ client, message: 'Bea returned nothing for this client.' });
                return;
            }
            const { verdict, reason, sourceTitle, sourceTitleIsExplicit } = parseVerdict(finalAnswer);
            this.updateRow(client, row => ({ ...row, status: 'answered', verdict, answer: reason, sourceTitle }));

            // Only for clients that actually matched, and only when a document was named: the link
            // is the point of the result, but it is not worth a round trip for a client the answer
            // has already ruled out.
            let resolved = false;
            if (sourceTitle && verdict !== 'no') {
                resolved = await this.resolveSource({ client, sourceTitle });
            }

            // A candidate that named no real document was the model finishing its sentence, not
            // citing a file, so the words go back where they came from. Only for titles that were
            // not explicitly file names: one ending in ".pdf" was a citation either way, and reading
            // it as the end of a sentence is worse than dropping it.
            if (sourceTitle && !resolved && !sourceTitleIsExplicit) {
                this.updateRow(client, row => ({
                    ...row,
                    answer: [reason, sourceTitle].filter(Boolean).join(' '),
                    sourceTitle: undefined
                }));
            }
        } catch (error: any) {
            // Somebody pressing Stop is not a failure. Reported as one it is both wrong and loud: the
            // row renders "could not be read", which for a one-client search means the whole card
            // says "No clients matched" as though the record had been checked, and every stopped
            // client sends an AbortError to Sentry. The run's own signal is what tells the two apart,
            // because the per-client timeout below aborts without it.
            if (signal.aborted) {
                this.updateRow(client, row => ({ ...row, status: 'waiting' }));
                return;
            }

            // A failed client must stay visibly failed. Reporting it as an empty answer would read as
            // "nothing on file", which is exactly the wrong conclusion to hand a case manager.
            this.log.error(error, { context: 'caseload search failed for one client' });
            // The row's message sits under a client's name, so it is product copy. Whatever an
            // upstream service happened to write is not: it goes to Sentry above, where it is
            // useful, and never to a case manager.
            this.markUnavailable({ client, message: 'The search could not be completed for this client.' });
        } finally {
            clearTimeout(timeout);
            signal.removeEventListener('abort', abortThisClient);
        }
    };

    /**
     * Turns the file name the model reported into a link, by finding that file among the client's
     * own attachments. Nothing is linked unless a real attachment matches, so the row can never
     * offer to open a document that does not exist. Failure is silent: the answer still stands, it
     * just loses its shortcut.
     */
    private resolveSource = async (params: { client: ICaseloadClient; sourceTitle: string }): Promise<boolean> => {
        const { client, sourceTitle } = params;

        try {
            const lightCareMap = this.orgState.getOrganizationCareMap(client.careMapKey.careMapId);
            if (!lightCareMap?.links?.self) {
                return false;
            }

            const careMap: WithLinks<ICareMap> = await this.apiClient.get(lightCareMap.links.self, CAREMAP_MEDIA_TYPE);
            if (!careMap.links?.notes) {
                return false;
            }

            const notes: ICaremapNoteResponse[] = await this.apiClient.get(careMap.links.notes, NOTE_MEDIA_TYPE);

            const wanted = normaliseFileName(sourceTitle);
            for (const note of notes) {
                for (const attachment of note.attachments || []) {
                    const candidate = normaliseFileName(attachment.fileName);
                    // The model tends to shorten or re-punctuate a file name, so an exact match is
                    // too strict; containment either way is close enough to be confident.
                    if (candidate === wanted || candidate.includes(wanted) || wanted.includes(candidate)) {
                        this.updateRow(client, row => ({
                            ...row,
                            source: { fileName: attachment.fileName, url: attachment.url }
                        }));
                        return true;
                    }
                }
            }
        } catch (error: any) {
            this.log.error(error, { context: 'caseload search could not resolve a source document' });
        }

        return false;
    };

    private markUnavailable = (params: { client: ICaseloadClient; message: string }) => {
        const { client, message } = params;
        this.updateRow(client, row => ({ ...row, status: 'unavailable', answer: '', failureMessage: message }));
    };

    private updateRow = (client: ICaseloadClient, update: (row: ICaseloadSearchRow) => ICaseloadSearchRow) => {
        const rows = this.state.rows.map(row => {
            const isSameClient = row.client.careMapKey.userId === client.careMapKey.userId
                && row.client.careMapKey.careMapId === client.careMapKey.careMapId;
            return isSameClient ? update(row) : row;
        });
        this.setState({ rows });
    };

    private searchWithConcurrencyLimit = async (params: {
        clients: ICaseloadClient[];
        run: (client: ICaseloadClient) => Promise<void>;
        signal: AbortSignal;
    }): Promise<void> => {
        const { clients, run, signal } = params;

        let nextIndex = 0;
        const startWorker = async (): Promise<void> => {
            // Checked each time round rather than only at the start: Stop has to end the run, and a
            // worker holding a queue of twenty clients would otherwise work through all of them.
            while (nextIndex < clients.length && !signal.aborted) {
                const client = clients[nextIndex];
                nextIndex++;
                await run(client);
            }
        };

        const workerCount = Math.min(MAX_CONCURRENT_CLIENT_SEARCHES, clients.length);
        const workers: Promise<void>[] = [];
        for (let i = 0; i < workerCount; i++) {
            workers.push(startWorker());
        }
        await Promise.all(workers);
    };
}
